import { toast } from "@/components/toast/CustomToast";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import useStore from "@/store/store";
import type { ToasterProps } from "sonner";

type RosterStatus = "QUEUED" | "RUNNING" | "SUCCESS" | "FAILED";

interface ShowRosterToastParams {
  status: RosterStatus;
  errorMessage?: string;
}

// 토스트 ID 관리 (중복 방지)
let currentToastId: string | number | undefined;

/**
 * 상태에 따라 근무표 생성 토스트를 표시
 */
export function showRosterToast({
  status,
  errorMessage,
}: ShowRosterToastParams) {
  const { resetRosterMadeState } = useStore.getState();

  // 이전 토스트 닫기
  if (currentToastId) {
    toast.dismiss(currentToastId);
  }

  const position: ToasterProps["position"] = "top-right";

  switch (status) {
    case "QUEUED":
      currentToastId = toast.loading("잠시만 기다려주세요", {
        description: "최적의 근무 조합을 계산하고 있습니다.",
        position,
        duration: Infinity,
      });
      break;

    case "RUNNING":
      currentToastId = toast.loading("근무표를 생성 중입니다...", {
        description: "근무표를 생성중입니다. 잠시만 기다려주세요.",
        position,
        duration: Infinity,
      });
      break;

    case "SUCCESS":
      currentToastId = toast.success("근무표 생성 완료", {
        description: (
          <div className="flex flex-col gap-1">
            <p>성공적으로 근무표가 반영되었습니다.</p>
            <div className="mt-1">
              <Button
                asChild
                className="flex items-center gap-2 border border-zinc-50/50 bg-white/20! hover:bg-white/10! h-8 text-xs"
              >
                <Link
                  to="/roster_create"
                  onClick={() => {
                    resetRosterMadeState();
                    toast.dismiss();
                  }}
                >
                  근무표 보러가기 <ChevronRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        ),
        className: "bg-gradient-to-r from-[#07ACE3] to-purple-500 text-white",
        position,
        duration: Infinity,
      });
      break;

    case "FAILED":
      currentToastId = toast.error("근무표 생성 실패", {
        description: errorMessage || "알 수 없는 오류가 발생했습니다.",
        className: "bg-red-500 text-white",
        position,
        duration: Infinity,
      });
      break;
  }

  return currentToastId;
}

/**
 * 현재 토스트 닫기
 */
export function dismissRosterToast() {
  if (currentToastId) {
    toast.dismiss(currentToastId);
    currentToastId = undefined;
  }
}
